import { useRef, useState, useEffect } from 'react';
import { CopyIconSmall } from '../assets/icons/UserProfileIcons';
import { copyToClipboard } from '../helpers/copyText';
import { toast } from 'sonner';

export default function TruncatedTextWithTooltip({ text, textClassName = '', containerClassName = '', ...props }) {
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    const textRef = useRef(null);

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

    useEffect(() => {
        if (showTooltip && containerRef.current && tooltipRef.current) {
            const textRect = containerRef.current.getBoundingClientRect();

            // Start tooltip from bottom-right corner of the target
            const left = textRect.right - 14;
            const top = textRect.bottom;

            setTooltipStyle({
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 1000,
            });
        }
    }, [showTooltip]);

    useEffect(() => {
        requestAnimationFrame(() => {
            const el = textRef.current;

            if (!el) return;

            const isTruncated = el.scrollWidth > el.clientWidth;
            setShouldShowTooltip(isTruncated);
        });
    }, [text]);

    useEffect(() => {
        if (!showTooltip) return;

        let hideTimeout;

        const handleDocumentMouseMove = (e) => {
            const isInside = containerRef.current?.contains(e.target) || tooltipRef.current?.contains(e.target);

            if (isInside) {
                // If mouse is over container or tooltip, keep it open
                if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    hideTimeout = null;
                }
            } else {
                // If mouse is outside, start hide timer if not already started
                if (!hideTimeout) {
                    hideTimeout = setTimeout(() => {
                        setShowTooltip(false);
                        hideTimeout = null;
                    }, 200);
                }
            }
        };

        document.addEventListener('mousemove', handleDocumentMouseMove);

        return () => {
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
        };
    }, [showTooltip]);

    return (
        <div
            ref={containerRef}
            className={`w-full overflow-hidden whitespace-nowrap text-ellipsis cursor-default ${containerClassName}`}
            style={{ maxWidth: '100%' }}
            onMouseEnter={() => shouldShowTooltip && setShowTooltip(true)}
        >
            <p ref={textRef} className={`truncate w-full ${textClassName}`} {...props}>
                {text}
            </p>

            {showTooltip && (
                <div
                    ref={tooltipRef}
                    className="fixed bg-grayBgNormal border border-borderUnderline   text-xs px-2 py-2 leading-5 rounded shadow-lg whitespace-nowrap"
                    style={tooltipStyle}
                >
                    <div className="flex items-center gap-2">
                        <div
                            onClick={() => {
                                copyToClipboard(text);
                                toast.success('Copied to clipboard.');
                            }}
                            className="cursor-pointer"
                        >
                            <div className=" rounded-[2px] p-1 ">
                                <CopyIconSmall />
                            </div>
                        </div>
                        <span> {text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
